/**
 * The combat HUD.
 *
 * Everything that changes per frame — bars, timers, cooldown sweeps, the
 * waveform, the danger ring — is written straight to DOM refs from one
 * `requestAnimationFrame` loop. React only re-renders when the *set* of spells,
 * the round, or an announcement changes, which is a handful of times a match.
 *
 * Information priority, top to bottom, matches the brief: incoming attack →
 * parry readiness → your own sigil → selected spell and cast progress →
 * vitals and cooldowns → decoration.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { TIMING } from '@spell/shared-types';
import { chantTokenProgress } from '@spell/combat-rules';
import { normalizeChant } from '@spell/spell-schema';
import { useNet, selectOpponent, selectSelf } from '../networking/netStore';
import { scene as bus } from '../scene/sceneBus';
import { castState, useCastUi } from '../gameplay/castState';
import { micLevels } from '../voice/mic';
import { net } from '../networking/net';
import { ParryTiming } from './ParryTiming';

const WAVE_BARS = 28;

export function Hud() {
  const phase = useNet((s) => s.phase);
  const round = useNet((s) => s.round);
  const mode = useNet((s) => s.mode);
  const slots = useNet((s) => s.slots);
  const ownSpells = useNet((s) => s.ownSpells);
  const announcement = useNet((s) => s.announcement);
  /**
   * Which spell the client currently believes is being performed.
   *
   * Nothing selects a spell any more, so this follows the live guess in
   * `castState` — pulled into React state rather than read per frame because it
   * changes a handful of times per cast, not sixty times a second, and the word
   * checklist below has to be rebuilt when it does.
   */
  const [guessId, setGuessId] = useState('');
  const self = useNet(selectSelf);
  const opponent = useNet(selectOpponent);
  const lastResult = useCastUi((s) => s.lastResult);
  const usingFallback = useCastUi((s) => s.usingFallbackInput);
  const typedChant = useCastUi((s) => s.typedChant);
  const setTypedChant = useCastUi((s) => s.setTypedChant);

  const refs = {
    selfHp: useRef<HTMLDivElement>(null),
    selfHpText: useRef<HTMLDivElement>(null),
    selfMana: useRef<HTMLDivElement>(null),
    selfShield: useRef<HTMLDivElement>(null),
    oppHp: useRef<HTMLDivElement>(null),
    oppHpText: useRef<HTMLDivElement>(null),
    oppMana: useRef<HTMLDivElement>(null),
    timer: useRef<HTMLDivElement>(null),
    chant: useRef<HTMLDivElement>(null),
    castFill: useRef<HTMLDivElement>(null),
    castLabel: useRef<HTMLDivElement>(null),
    compass: useRef<HTMLDivElement>(null),
    compassArrow: useRef<HTMLDivElement>(null),
    parry: useRef<HTMLDivElement>(null),
    flash: useRef<HTMLDivElement>(null),
  };
  const waveRefs = useRef<HTMLDivElement[]>([]);
  const tokenRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const slotRefs = useRef<Record<string, { cd: HTMLDivElement | null; mana: HTMLDivElement | null }>>({});

  const guessedSpell = ownSpells[guessId];

  /**
   * The guessed chant, split into the words the scorer matches one by one.
   * React owns the list; the animation loop only lights them.
   */
  const chantTokens = useMemo(
    () => (guessedSpell?.normalizedChant ?? '').split(' ').filter(Boolean),
    [guessedSpell?.normalizedChant],
  );
  // Drop refs belonging to a chant that is no longer the candidate.
  tokenRefs.current.length = chantTokens.length;

  useEffect(() => {
    let raf = 0;
    let lastHp = bus.selfHp;
    let flashUntil = 0;
    let lastGuess = '';

    const loop = () => {
      raf = requestAnimationFrame(loop);
      const now = performance.now();

      // --- vitals -------------------------------------------------------
      setScale(refs.selfHp.current, bus.selfHp / Math.max(1, bus.selfMaxHp));
      setText(refs.selfHpText.current, `${Math.ceil(bus.selfHp)}`);
      setScale(refs.selfMana.current, bus.selfMana / Math.max(1, bus.selfMaxMana));
      setScale(refs.selfShield.current, Math.min(1, bus.selfShield / 40));
      setScale(refs.oppHp.current, bus.oppHp / Math.max(1, bus.oppMaxHp));
      setText(refs.oppHpText.current, `${Math.ceil(bus.oppHp)}`);
      setScale(refs.oppMana.current, bus.oppMana / Math.max(1, bus.oppMaxMana));

      // --- damage flash --------------------------------------------------
      if (bus.selfHp < lastHp - 0.01) flashUntil = now + 260;
      lastHp = bus.selfHp;
      if (refs.flash.current) {
        const strength = flashUntil > now ? (flashUntil - now) / 260 : 0;
        refs.flash.current.style.boxShadow = `inset 0 0 170px rgba(255,60,40,${(strength * 0.55).toFixed(3)})`;
      }

      // --- timer ---------------------------------------------------------
      const state = useNet.getState();
      const remaining = Math.max(0, state.phaseEndsAt - (Date.now() + net.clockOffset));
      if (refs.timer.current) {
        // A practice round has no clock at all — `phaseEndsAt` is zero there,
        // which would otherwise read as a permanently expired timer.
        const endless = state.phase === 'round' && state.phaseEndsAt === 0;
        const seconds = Math.ceil(remaining / 1000);
        refs.timer.current.textContent = endless ? '∞' : state.phase === 'round' ? `${seconds}` : '—';
        refs.timer.current.classList.toggle(
          'round-timer--urgent',
          state.phase === 'round' && !endless && seconds <= 10,
        );
      }

      // --- which spell does this look like? -------------------------------
      // The only per-frame value allowed to cause a React render, and it can
      // only do so when the answer actually changes — a handful of times per
      // cast. The word checklist and the highlighted row both hang off it.
      if (castState.guessId !== lastGuess) {
        lastGuess = castState.guessId;
        setGuessId(castState.guessId);
      }

      // --- chant ---------------------------------------------------------
      // What the recogniser has heard so far, or the typed chant when speech is
      // unavailable. An empty string during a cast is its own state: the
      // player is holding the key and nothing has landed yet, and saying so
      // ("듣는 중…") is the difference between a quiet mic and a dead one.
      const castUi = useCastUi.getState();
      const spoken = castState.active
        ? castState.transcript || (castUi.usingFallbackInput ? castUi.typedChant : '')
        : '';
      if (refs.chant.current) {
        const el = refs.chant.current;
        setText(el, castState.active ? spoken || '듣는 중…' : '');
        const chantState = !castState.active ? 'idle' : spoken ? 'heard' : 'listening';
        if (el.dataset.state !== chantState) el.dataset.state = chantState;
      }

      // Light each word of the registered chant as the scorer credits it. The
      // matcher is the server's, not a lookalike — see `chantTokenProgress`.
      if (tokenRefs.current.length > 0) {
        const target = state.ownSpells[castState.guessId]?.normalizedChant ?? '';
        const tokens =
          castState.active && spoken && target
            ? chantTokenProgress(normalizeChant(spoken), target)
            : null;
        for (let i = 0; i < tokenRefs.current.length; i++) {
          const el = tokenRefs.current[i];
          if (!el) continue;
          const token = tokens?.[i];
          const tokenState = !castState.active
            ? 'idle'
            : token?.heard
              ? 'heard'
              : token && token.score >= 0.3
                ? 'near'
                : 'pending';
          if (el.dataset.state === tokenState) continue;
          el.dataset.state = tokenState;
          const mark = el.querySelector('[data-token-mark]');
          if (mark) {
            mark.textContent = tokenState === 'heard' ? '✓' : tokenState === 'near' ? '~' : '○';
          }
        }
      }

      // --- channel bar ---------------------------------------------------
      // The same build-up the magic circle is drawing, read as a number: how
      // far through the incantation you are, and therefore how long you have
      // been standing still with your camera pinned.
      const castProgress = bus.circle.active ? bus.circle.progress : 0;
      setScale(refs.castFill.current, castProgress);
      if (refs.castLabel.current) {
        setText(
          refs.castLabel.current,
          castState.active ? `영창 ${Math.round(castProgress * 100)}%` : '',
        );
        refs.castLabel.current.classList.toggle('cast-progress__label--full', castProgress >= 0.999);
      }

      // --- waveform ------------------------------------------------------
      const level = Math.min(1, micLevels.level * 7);
      waveRefs.current.forEach((bar, i) => {
        if (!bar) return;
        const phaseOffset = Math.sin(now / 90 + i * 0.55) * 0.5 + 0.5;
        const h = 3 + level * 34 * (0.35 + phaseOffset * 0.65);
        bar.style.height = `${h.toFixed(1)}px`;
        bar.style.opacity = castState.active ? '1' : '0.35';
      });

      // --- threat compass --------------------------------------------------
      // The danger ring itself belongs to <ParryTiming>, which owns everything
      // drawn at the reticle so the two rings cannot disagree about a shot.
      const incoming = bus.projectiles.filter((p) => p.incoming);
      const nearest = incoming.reduce<null | (typeof incoming)[number]>(
        (best, p) => (best === null || p.eta < best.eta ? p : best),
        null,
      );
      if (refs.compass.current && refs.compassArrow.current) {
        if (nearest) {
          // Bearing of the threat relative to where the player is looking.
          const dx = nearest.x - 0;
          const dz = nearest.z - bus.selfSide * 7;
          const facing = bus.selfSide > 0 ? Math.atan2(dx, -dz) : Math.atan2(-dx, dz);
          const relative = facing - bus.yaw;
          const offscreen = Math.abs(relative) > 0.52;
          refs.compass.current.style.opacity = offscreen ? '1' : '0';
          refs.compassArrow.current.style.transform = `translate(-50%, -130px) rotate(${(relative * 180) / Math.PI}deg)`;
        } else {
          refs.compass.current.style.opacity = '0';
        }
      }

      // --- parry readiness -------------------------------------------------
      // Threshold is PARRY_WINDOW_MS alone. The latency grace is slack on the
      // *late* side of impact, so folding it in here lit the badge 150 ms
      // before the window actually opened — the badge was teaching a beat the
      // server rejects.
      if (refs.parry.current) {
        const opensAt = TIMING.PARRY_WINDOW_MS / 1000;
        const parryable = incoming.filter((p) => p.parryable);
        const holding = parryable.find((p) => p.resolveIn > 0) ?? null;
        const closest = parryable.reduce<null | (typeof parryable)[number]>(
          (best, p) => (best === null || p.eta < best.eta ? p : best),
          null,
        );
        const ready = Boolean(holding) || Boolean(closest && closest.eta <= opensAt);
        refs.parry.current.classList.toggle('parry-badge--ready', ready);
        refs.parry.current.classList.toggle('parry-badge--last', Boolean(holding));
        const label = refs.parry.current.querySelector('[data-parry-label]');
        if (label) {
          label.textContent = holding
            ? '마지막 기회!'
            : ready
              ? '지금 패링!'
              : nearest
                ? '공격 접근 중'
                : '패링 대기';
        }
      }

      // --- cooldowns ------------------------------------------------------
      const room = net.room;
      if (room) {
        const roomState = room.state as unknown as {
          serverTime: number;
          players: { get(id: string): { spells: { get(id: string): { readyAt: number } | undefined } } | undefined };
        };
        const player = roomState.players?.get(net.sessionId);
        for (const id of Object.keys(slotRefs.current)) {
          const el = slotRefs.current[id];
          const spell = useNet.getState().ownSpells[id];
          if (!el || !spell) continue;
          const summary = player?.spells?.get(id);
          const readyAt = summary?.readyAt ?? 0;
          const remainingCd = Math.max(0, readyAt - roomState.serverTime);
          const ratio = spell.stats.cooldownMs > 0 ? remainingCd / spell.stats.cooldownMs : 0;
          if (el.cd) el.cd.style.transform = `scaleY(${Math.min(1, ratio).toFixed(3)})`;
          if (el.mana) {
            const affordable = bus.selfMana >= spell.stats.manaCost;
            el.mana.style.transform = `scaleX(${Math.min(1, spell.stats.manaCost / 100).toFixed(3)})`;
            el.mana.style.background = affordable ? '#b78bff' : '#ff5a45';
          }
        }
      }
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // Refs are stable; this loop intentionally runs for the whole match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const waveBars = useMemo(() => Array.from({ length: WAVE_BARS }, (_, i) => i), []);
  const selfScore = self?.score ?? 0;
  const oppScore = opponent?.score ?? 0;

  return (
    <div className="hud" data-testid="hud">
      <div className="hud__flash" ref={refs.flash} />

      {/* Vitals — self */}
      <div className="hud__vitals hud__vitals--self">
        <div className="vital-name">
          <span aria-hidden>✦</span>
          <span>{self?.name ?? '나'}</span>
        </div>
        <div className="bar">
          <div className="bar__fill bar__fill--hp" ref={refs.selfHp} data-testid="self-hp" />
          <div className="bar__value" ref={refs.selfHpText}>
            100
          </div>
        </div>
        <div className="bar bar--slim">
          <div className="bar__fill bar__fill--mana" ref={refs.selfMana} />
        </div>
        <div className="bar bar--slim">
          <div className="bar__fill bar__fill--shield" ref={refs.selfShield} style={{ transform: 'scaleX(0)' }} />
        </div>
      </div>

      {/* Vitals — opponent */}
      <div className="hud__vitals hud__vitals--enemy">
        <div className="vital-name">
          <span>{opponent?.name ?? '상대'}</span>
          <span aria-hidden>✧</span>
        </div>
        <div className="bar">
          <div className="bar__fill bar__fill--hp-enemy" ref={refs.oppHp} data-testid="opp-hp" />
          <div className="bar__value" ref={refs.oppHpText}>
            100
          </div>
        </div>
        <div className="bar bar--slim">
          <div className="bar__fill bar__fill--mana" ref={refs.oppMana} />
        </div>
        {opponent?.usingFallbackInput ? (
          <div className="hint" style={{ marginTop: 4 }}>
            ⌨ 상대는 키보드 영창 사용 중
          </div>
        ) : null}
      </div>

      {/* Round header */}
      <div className="hud__round">
        <div className="round-label">
          {mode === 'practice'
            ? 'PRACTICE'
            : phase === 'round'
              ? round >= 3
                ? 'FINAL ROUND'
                : `ROUND ${round}`
              : phaseLabel(phase)}
        </div>
        <div className="round-timer" ref={refs.timer} data-testid="round-timer">
          —
        </div>
        {/* Nothing is being won in the practice range, so nothing is scored. */}
        {mode === 'practice' ? null : (
          <div className="round-score">
            {[0, 1].map((i) => (
              <span key={`s${i}`} className={`pip ${i < selfScore ? 'pip--self' : 'pip--empty'}`} />
            ))}
            {[0, 1].map((i) => (
              <span key={`e${i}`} className={`pip ${i < oppScore ? 'pip--enemy' : 'pip--empty'}`} />
            ))}
          </div>
        )}
      </div>

      {/* Reticle + parry timing ring */}
      <ParryTiming />

      {/* Off-screen threat indicator */}
      <div className="hud__compass" ref={refs.compass}>
        <div className="compass-arrow" ref={refs.compassArrow} />
      </div>

      {/* The player's whole repertoire.
          Not a hotbar — nothing here can be selected. It is the reference sheet
          for a game where the way to cast a spell is to remember its
          incantation, plus a live readout of which one the sigil currently
          looks like. */}
      <div className="hud__slots" data-testid="spell-slots">
        {slots.map((id) => {
          const spell = ownSpells[id];
          if (!spell) return null;
          const active = id === guessId;
          return (
            <div
              key={id}
              className={`slot ${active ? 'slot--active' : ''}`}
              data-spell-id={id}
              data-guess={active ? 'true' : undefined}
            >
              <SlotGlyph type={spell.type} color={spell.visuals.color} />
              <span className="slot__name">{spell.chant}</span>
              <div
                className="slot__cd"
                ref={(el) => {
                  slotRefs.current[id] = { ...(slotRefs.current[id] ?? { cd: null, mana: null }), cd: el };
                }}
              />
              <div
                className="slot__mana"
                ref={(el) => {
                  slotRefs.current[id] = { ...(slotRefs.current[id] ?? { cd: null, mana: null }), mana: el };
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Voice. While a chant is running this is the loudest panel on screen —
          it is the one thing the player is actively performing, and it used to
          be a 15px line they never looked at. */}
      <div className="hud__chant">
        {/* The registered chant, word by word, lighting up as each one lands. */}
        <div className="chant-tokens" data-testid="chant-tokens">
          {chantTokens.length > 0 ? (
            chantTokens.map((word, i) => (
              <span
                key={`${word}-${i}`}
                className="chant-token"
                data-state="idle"
                ref={(el) => {
                  tokenRefs.current[i] = el;
                }}
              >
                <b>{word}</b>
                <i data-token-mark>○</i>
              </span>
            ))
          ) : (
            // Every castable spell has a chant now, so this is only ever the
            // empty-book case.
            <span className="chant-token chant-token--empty">주문 없음</span>
          )}
        </div>

        <div className="waveform">
          {waveBars.map((i) => (
            <div
              key={i}
              className="waveform__bar"
              ref={(el) => {
                if (el) waveRefs.current[i] = el;
              }}
            />
          ))}
        </div>
        <div className="chant-text" ref={refs.chant} data-state="idle" data-testid="chant-text" />

        <div className="cast-progress">
          <div className="cast-progress__fill" ref={refs.castFill} style={{ width: '100%', transform: 'scaleX(0)' }} />
        </div>
        <div className="cast-progress__label" ref={refs.castLabel} />

        {usingFallback ? (
          <>
            <div className="chant-fallback-note">🎙 음성 인식 불가 — 키보드 영창으로 진행 중</div>
            <input
              className="field fallback-input"
              data-testid="fallback-chant"
              placeholder="영창을 입력하세요 (음성 대체 입력)"
              value={typedChant}
              onChange={(e) => setTypedChant(e.target.value)}
            />
          </>
        ) : null}
      </div>

      {/* Parry readiness */}
      <div className="hud__parry">
        <div className="parry-badge" ref={refs.parry} data-testid="parry-badge">
          <span aria-hidden>◈</span>
          <span data-parry-label>패링 대기</span>
        </div>
        <div className="hint gesture-hint">
          누른 채로 = 시전 · 외치고 그린 뒤 손을 뗍니다
          <br />
          시전 중 Shift = 획 나누기 · 주문은 영창과 문양으로 결정됩니다
        </div>
      </div>

      {/* Announcement */}
      {announcement ? (
        <div className="hud__announce" key={announcement.at}>
          {announcement.text}
        </div>
      ) : null}

      {/* Grade popup */}
      {lastResult && Date.now() - lastResult.at < 1400 ? (
        <div className="hud__grade" key={lastResult.at}>
          <div className={`grade-word grade-word--${lastResult.grade}`}>
            {lastResult.parry && lastResult.grade !== 'Fail' ? `PARRY ${lastResult.grade}` : lastResult.grade}
          </div>
          <div className="grade-detail">
            {lastResult.reason
              ? failLabel(lastResult.reason)
              : `영창 ${Math.round(lastResult.chant * 100)}% · 문양 ${Math.round(lastResult.gesture * 100)}%${
                  lastResult.parry ? ` · 타이밍 ${lastResult.timingError > 0 ? '+' : ''}${Math.round(lastResult.timingError)}ms` : ''
                }`}
          </div>
          {/* Without this, a rejected cast is a dead end: the player cannot
              tell a dropped word from a wrong one, so there is nothing to
              correct on the next attempt. */}
          {lastResult.expected && (lastResult.grade === 'Fail' || lastResult.grade === 'Weak') ? (
            <div className="grade-diff" data-testid="grade-diff">
              <div>
                <span>들린 것</span>
                <b className="grade-diff__heard">
                  {normalizeChant(lastResult.heard) || '(들리지 않음)'}
                </b>
              </div>
              <div>
                <span>필요한 영창</span>
                <b>{lastResult.expected}</b>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SlotGlyph({ type, color }: { type: string; color: string }) {
  // Shape carries the meaning; colour is a second channel, never the only one.
  if (type === 'parry') {
    return (
      <svg className="slot__glyph" viewBox="0 0 24 24" aria-label="패링 주문">
        <path d="M12 2 L21 6 V12 C21 17 17 21 12 22 C7 21 3 17 3 12 V6 Z" fill="none" stroke={color} strokeWidth="1.6" />
        <path d="M12 7 V17" stroke={color} strokeWidth="1.2" />
      </svg>
    );
  }
  return (
    <svg className="slot__glyph" viewBox="0 0 24 24" aria-label="시그니처 주문">
      <path d="M12 2 L15 9 L22 12 L15 15 L12 22 L9 15 L2 12 L9 9 Z" fill="none" stroke={color} strokeWidth="1.6" />
    </svg>
  );
}

function phaseLabel(phase: string): string {
  switch (phase) {
    case 'waiting':
      return '상대 대기 중';
    case 'check':
      return '연결 확인';
    case 'countdown':
      return '준비';
    case 'roundEnd':
      return '라운드 종료';
    case 'craft':
      return '주문 제작';
    case 'matchEnd':
      return '경기 종료';
    case 'aborted':
      return '경기 중단';
    default:
      return phase;
  }
}

function failLabel(reason: string): string {
  switch (reason) {
    case 'recognition':
      return '영창 또는 문양을 알아보지 못했습니다';
    case 'mana':
      return '마나가 부족합니다';
    case 'cooldown':
      return '아직 재사용 대기 중입니다';
    case 'stunned':
      return '기절 상태입니다';
    case 'no-target':
      return '막을 공격이 없었습니다';
    case 'ambiguous':
      return '어느 주문인지 알 수 없었습니다 — 영창과 문양을 또렷하게';
    case 'timestamp':
      return '타이밍이 판정 범위를 벗어났습니다';
    case 'phase':
      return '지금은 시전할 수 없습니다';
    default:
      return reason;
  }
}

function setScale(el: HTMLElement | null, ratio: number) {
  if (!el) return;
  el.style.transform = `scaleX(${Math.max(0, Math.min(1, ratio)).toFixed(4)})`;
}

function setText(el: HTMLElement | null, text: string) {
  if (el && el.textContent !== text) el.textContent = text;
}
